import axios from "axios";

export const API_BASE =
  process.env.REACT_APP_API_BASE_URL || "https://api.sunpledgeholdings.com/api";

const ACCESS_KEY = "sunpledge_admin_access";
const REFRESH_KEY = "sunpledge_admin_refresh";

const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  timeout: 20000,
});

function saveTokens({ accessToken, refreshToken }) {
  if (accessToken) localStorage.setItem(ACCESS_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
}

function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export function getAccessToken() {
  return localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_KEY);
}

export function getErrorMessage(error, fallback = "Something went wrong") {
  if (!error?.response) {
    return error?.message === "Network Error"
      ? "Cannot reach the server. Check that the API is running."
      : error?.message || fallback;
  }
  return error.response.data?.message || fallback;
}

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshPromise = null;

export async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token");
  const { data } = await axios.post(
    `${API_BASE}/auth/refresh`,
    { refreshToken },
    { timeout: 20000 },
  );
  if (!data.accessToken) throw new Error("No access token returned");
  saveTokens({ accessToken: data.accessToken });
  return data.accessToken;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const request = error.config;
    const isAuthRoute =
      request?.url?.includes("/auth/login") ||
      request?.url?.includes("/auth/refresh");

    if (error.response?.status === 401 && request && !request._retry && !isAuthRoute) {
      request._retry = true;
      try {
        if (!refreshPromise) refreshPromise = refreshAccessToken();
        const accessToken = await refreshPromise;
        request.headers.Authorization = `Bearer ${accessToken}`;
        return api(request);
      } catch (refreshError) {
        clearTokens();
        window.dispatchEvent(new Event("sunpledge:session-expired"));
        return Promise.reject(refreshError);
      } finally {
        refreshPromise = null;
      }
    }
    return Promise.reject(error);
  },
);

export async function login(email, password) {
  const { data } = await api.post("/auth/login", { email, password });
  saveTokens(data);
  return data;
}

export async function resetPassword(currentPassword, newPassword) {
  const { data } = await api.patch("/auth/reset-password", {
    currentPassword,
    newPassword,
  });
  if (!data.accessToken || !data.refreshToken) {
    throw new Error("Password changed, but the server did not return a complete session");
  }
  saveTokens(data);
  return data;
}

export async function registerUser(email, password) {
  await api.post("/auth/register", { email, password });
  // The backend may return internal user fields. Deliberately do not expose them.
  return { message: "Administrator account created successfully" };
}

export async function getUsers() {
  const { data } = await api.get("/auth/users");
  return data;
}

export async function deleteUser(id) {
  const { data } = await api.delete(`/auth/users/${id}`);
  return data;
}

export function logout() {
  clearTokens();
}

export async function uploadImage(file, onProgress) {
  const form = new FormData();
  form.append("image", file);
  const { data } = await axios.post(`${API_BASE}/upload`, form, {
    timeout: 60000,
    onUploadProgress: onProgress,
  });
  return data;
}

export async function getProjects() {
  const { data } = await api.get("/projects");
  return data;
}

export async function getProject(id) {
  const { data } = await api.get(`/projects/${id}`);
  return data;
}

export async function createProject(payload) {
  const { data } = await api.post("/projects", payload);
  return data;
}

export async function updateProject(id, payload) {
  const { data } = await api.put(`/projects/${id}`, payload);
  return data;
}

export async function deleteProject(id) {
  const { data } = await api.delete(`/projects/${id}`);
  return data;
}

export async function deleteProjectImage(id, imageUrl) {
  const { data } = await api.delete(`/projects/${id}/images`, {
    data: { imageUrl },
  });
  return data;
}

export async function getProjectCategories() {
  const { data } = await api.get("/project-categories");
  return data;
}

export async function getProjectCategory(id) {
  const { data } = await api.get(`/project-categories/${id}`);
  return data;
}

export async function createProjectCategory(payload) {
  const { data } = await api.post("/project-categories", payload);
  return data;
}

export async function updateProjectCategory(id, payload) {
  const { data } = await api.put(`/project-categories/${id}`, payload);
  return data;
}

export async function deleteProjectCategory(id) {
  const { data } = await api.delete(`/project-categories/${id}`);
  return data;
}

const adminApi = {
  login,
  resetPassword,
  registerUser,
  getUsers,
  deleteUser,
  logout,
  getAccessToken,
  getRefreshToken,
  refreshAccessToken,
  uploadImage,
  getProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
  deleteProjectImage,
  getProjectCategories,
  getProjectCategory,
  createProjectCategory,
  updateProjectCategory,
  deleteProjectCategory,
  getErrorMessage,
};

export default adminApi;
